/**
 * scheduleGanttTooltip.ts
 * Tooltip config for the Master-Gantt (extracted from ScheduleGantt.tsx to keep
 * that file under the 600-line cap).
 *
 * Builds a curated, human-readable tooltip that FOLLOWS THE POINTER
 * (BUG-2026-0194). It used to be pinned to the top-right corner of the plot
 * area, which is off-screen the moment the user scrolls down a tall chart;
 * following the cursor keeps it next to the hovered bar. Highcharts renders it
 * `outside` the SVG automatically when the plot area scrolls (scrollable plot
 * area), clamps it to the viewport and offsets it by the scroll position, so no
 * custom positioner is needed.
 *
 * Shows ONLY friendly fields — never any *Key (bookingKey, machineKey,
 * scheduleTaskKey, userKey, assignmentKey, projectKey, workOrderKey) and never
 * the raw conflictType value. Full detail lives in the click-driven
 * GanttDetailDrawer (leaf bars only — group/summary rows are not clickable, so
 * their tooltip omits the "Click for details" hint).
 *
 * Theme colors are passed in as a token bag so this module stays theme-free
 * (no MUI / theme import) — no hex literals here.
 */

import Highcharts from 'highcharts/highstock';

/** Theme color/typography tokens the tooltip needs (all from theme.*). */
export interface GanttTooltipTokens {
  borderColor: string;
  backgroundColor: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  conflictHard: string;
  conflictSoft: string;
  fontFamily: string;
}

/** 'YYYY-MM-DD'-style human date from Unix ms; "—" when absent. */
function fmtDate(ms: number | undefined): string {
  return ms ? Highcharts.dateFormat('%e %b %Y', ms) : '—';
}

/** Leaf bars open the details drawer; envelope/group rows do not. */
export function isLeafTaskType(taskType: string | undefined): boolean {
  return taskType === 'machineBooking' || taskType === 'employeeAssignment';
}

/**
 * Build the Highcharts `tooltip` options object for the Master Gantt.
 * Pure factory — takes theme tokens, returns config.
 */
export function buildGanttTooltip(
  tokens: GanttTooltipTokens,
): Highcharts.TooltipOptions {
  return {
    headerFormat: '',
    useHTML: true,
    hideDelay: 80,
    borderWidth: 1,
    borderColor: tokens.borderColor,
    backgroundColor: tokens.backgroundColor,
    shadow: true,
    // BUG-2026-0194: track the cursor (stays visible however far the user has
    // scrolled) instead of pinning to the plot's top-right corner.
    followPointer: true,
    pointFormatter(this: Highcharts.Point): string {
      const p = this as Highcharts.Point & {
        custom?: {
          status?: string | null;
          source?: string | null;
          eventType?: string | null;
          conflictType?: 'hard' | 'soft' | null;
          /** Run Mode Phase C — 'continuous' machine bookings run 24/7. */
          runMode?: string | null;
          /** BUG-2026-0194 — allocation share of the job (null = unknown). */
          sharePercent?: number | null;
          /** BUG-2026-0194 — weekly rows folded into this bar. */
          weekCount?: number;
          /** BUG-2026-0193 — per-line prep bookings folded into one bar. */
          isPrepMerge?: boolean;
          prepStepCount?: number;
          prepStepBreakdown?: string;
          prepMachineCodes?: string[];
          prepTotalMinutes?: number | null;
        };
        start?: number;
        end?: number;
        taskType?: string;
      };

      const custom = p.custom ?? {};
      const isAssignment = p.taskType === 'employeeAssignment';
      const isContinuousBooking =
        p.taskType === 'machineBooking' && custom.runMode === 'continuous';

      // One friendly row per present field. *Key fields are never read.
      // Wave 3: the "Allocation: N%" row is retired — an assignment bar's span
      // IS the busy days (the person is busy, full-day, across the dates shown).
      const rows: string[] = [];
      if (custom.status) rows.push(`<b>Status:</b> ${custom.status}`);
      if (custom.source) rows.push(`<b>Source:</b> ${custom.source}`);
      if (isAssignment) {
        rows.push(`<b>Staffing:</b> busy on the covered working days`);
        // BUG-2026-0194: the stored allocation share is the only signal that a
        // person is a reviewer/approver (10% / 5%) rather than full-time.
        if (typeof custom.sharePercent === 'number' && custom.sharePercent < 100) {
          rows.push(`<b>Share of job:</b> ${custom.sharePercent}%`);
        }
        if (typeof custom.weekCount === 'number' && custom.weekCount > 1) {
          rows.push(`<b>Weeks:</b> ${custom.weekCount} weekly assignments shown as one bar`);
        }
      }
      if (isContinuousBooking) {
        // Run Mode Phase C: the bar's span IS calendar run-time (end is the
        // exclusive right edge, so the day count is the plain ms difference).
        const calDays =
          p.start != null && p.end != null && p.end > p.start
            ? Math.max(1, Math.round((p.end - p.start) / 86_400_000))
            : null;
        rows.push(
          // Lab answer Q2 (2026-07-10): the technician is booked every working
          // day of the run (was "staff attend setup and teardown").
          `<b>Run mode:</b> Runs continuously (24/7)${calDays ? ` for ${calDays} calendar day${calDays !== 1 ? 's' : ''}` : ''}; staffed every working day of the run`,
        );
      }
      if (custom.isPrepMerge && typeof custom.prepStepCount === 'number') {
        // BUG-2026-0193: the bar is the work order's whole prep effort — say
        // what it folds so nobody hunts for the individual cutting/grinding bars.
        const mins = typeof custom.prepTotalMinutes === 'number'
          ? ` · ${Math.round(custom.prepTotalMinutes)} min combined`
          : '';
        rows.push(`<b>Preparation:</b> ${custom.prepStepCount} prep steps shown as one bar (${custom.prepStepBreakdown ?? ''})${mins}`);
        if (custom.prepMachineCodes?.length) rows.push(`<b>Machines:</b> ${custom.prepMachineCodes.join(', ')}`);
      }
      if (custom.eventType) rows.push(`<b>Event:</b> ${custom.eventType}`);
      if (custom.conflictType) {
        const color =
          custom.conflictType === 'hard'
            ? tokens.conflictHard
            : tokens.conflictSoft;
        rows.push(`<span style="color:${color}">⚠ Conflict</span>`);
      }

      const detailRows = rows.length
        ? `<div style="margin-top:4px">${rows.join('<br/>')}</div>`
        : '';
      const hint = isLeafTaskType(p.taskType)
        ? `<div style="margin-top:6px;color:${tokens.textDisabled};font-size:11px">Click for details</div>`
        : '';

      return `
        <div style="font-size:13px;max-width:280px">
          <b>${p.name}</b><br/>
          <span style="color:${tokens.textSecondary}">${fmtDate(p.start)} – ${fmtDate(p.end)}</span>
          ${detailRows}
          ${hint}
        </div>
      `;
    },
    style: {
      color: tokens.textPrimary,
      fontFamily: tokens.fontFamily,
    },
  };
}
